import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Save, Trash2, Plus, X, Upload, Search } from 'lucide-react';
import './style/ExamSummary.css';

export default function ExamSummary() {
  const navigate = useNavigate();
  const addQuestionButtonRef = useRef(null);
  const addQuestionModalRef = useRef(null);

  const [examData, setExamData] = useState({
    title: 'Trigonometry Exam',
    description: 'This exam covers advanced trigonometry concepts.',
    chapterName: 'Advanced Trigonometry Concepts',
    topic: 'Trigonometry',
    questions: [
      { type: 'text', content: "In ABC, right angled at B, if AB = 12 cm and BC = 5 cm, find (i) sin A and tan A (ii) sin C and cot C.", marks: 5 },
      { type: 'mcq', content: "What is the value of sin 30°?", options: ["1/2", "√3/2", "1/√2", "1"], marks: 2 },
      { type: 'tf', content: "The value of cos 90° is 0.", marks: 1 },
      { type: 'text', content: "If 12/cos A = 13, verify that: 35 sin A(1 - tan A) = 156.", marks: 5 },
      { type: 'text', content: "(i) If 7 cot θ = 24, prove that (1 - cos θ)/(1 + cos θ) = 1/7\n(ii) If 4 cot θ = 5, show that: (5 sin θ + 3 cos θ)/(5 sin θ - 2 cos θ) = 2.", marks: 10 },
    ]
  });

  const [editingIndex, setEditingIndex] = useState(-1);
  const [editedQuestion, setEditedQuestion] = useState({ type: '', content: '', options: ['', '', '', ''], marks: 0 });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ type: '', content: '', options: ['', '', '', ''], marks: 0 });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addQuestionModalRef.current && !addQuestionModalRef.current.contains(event.target) &&
          !addQuestionButtonRef.current.contains(event.target)) {
        setIsAddModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleConfirm = () => {
    alert('Exam shared successfully!');
    navigate('/exams');
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const questionToEdit = examData.questions[index];
    setEditedQuestion({
      ...questionToEdit,
      options: questionToEdit.options || ['', '', '', '']
    });
  };

  const handleSave = (index) => {
    const updatedQuestions = [...examData.questions];
    updatedQuestions[index] = {
      ...editedQuestion,
      options: editedQuestion.type === 'mcq' ? editedQuestion.options : undefined
    };
    setExamData({ ...examData, questions: updatedQuestions });
    setEditingIndex(-1);
  };

  const handleDelete = (index) => {
    const updatedQuestions = examData.questions.filter((_, i) => i !== index);
    setExamData({ ...examData, questions: updatedQuestions });
  };

  const handleAddQuestion = () => {
    setIsAddModalOpen(true);
  };

  const handleSaveNewQuestion = () => {
    setExamData({
      ...examData,
      questions: [...examData.questions, newQuestion]
    });
    setIsAddModalOpen(false);
    setNewQuestion({ type: '', content: '', options: ['', '', '', ''], marks: 0 });
  };

  const handleMcqOptionChange = (index, value) => {
    const newOptions = [...newQuestion.options];
    newOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: newOptions });
  };

  const handleEditedMcqOptionChange = (index, value) => {
    const newOptions = [...editedQuestion.options];
    newOptions[index] = value;
    setEditedQuestion({ ...editedQuestion, options: newOptions });
  };

  const handleUploadAnswerKey = () => {
    setIsUploadModalOpen(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setUploadedFile(file);
  };

  const handleSaveUploadedFile = () => {
    if (uploadedFile) {
      console.log('File uploaded:', uploadedFile.name);
      alert('Answer key uploaded successfully!');
      setIsUploadModalOpen(false);
      setUploadedFile(null);
    }
  };

  const handleMarksChange = (index, value) => {
    const updatedQuestions = [...examData.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      marks: parseInt(value) || 0
    };
    setExamData({ ...examData, questions: updatedQuestions });
  };

  const filteredQuestions = examData.questions.filter(question =>
    question.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-blue-600 p-6 text-white">
          <h2 className="text-3xl font-bold text-center">Exam Summary</h2>
        </div>
        <div className="p-8 space-y-6">
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold text-gray-800">{examData.title}</h3>
            <p className="text-gray-600 italic">{examData.description}</p>
            <p className="text-gray-700">Chapter: {examData.chapterName}</p>
            <p className="text-gray-700">Topic: {examData.topic}</p>
          </div>
          
          <div className="space-y-6">
            <h4 className="text-xl font-semibold text-gray-800">Questions:</h4>
            <div className="flex items-center gap-4">
              <button 
                ref={addQuestionButtonRef}
                onClick={handleAddQuestion}
                className="flex items-center px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add New Question
              </button>
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              </div>
            </div>

            <ol className="space-y-6">
              {filteredQuestions.map((question, index) => (
                <li key={index} className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
                  {editingIndex === index ? (
                    <div className="p-6">
                      <select
                        value={editedQuestion.type}
                        onChange={(e) => setEditedQuestion({ ...editedQuestion, type: e.target.value })}
                        className="w-full p-2 mb-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="text">Text</option>
                        <option value="mcq">Multiple Choice</option>
                        <option value="tf">True/False</option>
                      </select>
                      <textarea
                        value={editedQuestion.content}
                        onChange={(e) => setEditedQuestion({ ...editedQuestion, content: e.target.value })}
                        className="w-full p-2 mb-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="4"
                      />
                      {editedQuestion.type === 'mcq' && (
                        <div className="space-y-2 mb-4">
                          {editedQuestion.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center">
                              <span className="w-8 text-gray-500 font-semibold">{String.fromCharCode(65 + optionIndex)}.</span>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => handleEditedMcqOptionChange(optionIndex, e.target.value)}
                                className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={`Option ${optionIndex + 1}`}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <input
                        type="number"
                        value={editedQuestion.marks}
                        onChange={(e) => setEditedQuestion({...editedQuestion, marks: parseInt(e.target.value) || 0})}
                        className="w-full p-2 mb-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Marks"
                      />
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => handleSave(index)}
                          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors shadow-sm"
                        >
                          <Save size={16} className="inline mr-2" />
                          Save
                        </button>
                        <button 
                          onClick={() => setEditingIndex(-1)}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors shadow-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-2">
                          <p className="text-lg text-gray-800">
                            <span className="font-semibold mr-2 text-blue-600">Question {index + 1}:</span>
                            {question.content}
                          </p>
                          <p className="text-sm text-gray-500 italic">Type: {question.type}</p>
                          <div className="flex items-center">
                            <span className="text-sm text-gray-500 italic mr-2">Marks:</span>
                            <input
                              type="number"
                              value={question.marks}
                              onChange={(e) => handleMarksChange(index, e.target.value)}
                              className="w-16 p-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          {question.type === 'mcq' && (
                            <div className="mt-3 ml-6 space-y-1">
                              {question.options.map((option, optionIndex) => (
                                <p key={optionIndex} className="text-gray-700">
                                  <span className="font-semibold mr-2">{String.fromCharCode(65 + optionIndex)}.</span>
                                  {option}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button 
                            onClick={() => handleEdit(index)}
                            className="p-2 text-blue-500 hover:text-blue-700 transition-colors"
                            aria-label="Edit question"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(index)}
                            className="p-2 text-red-500 hover:text-red-700 transition-colors"
                            aria-label="Delete question"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
        
        <div className="bg-gray-100 p-6 flex justify-between items-center">
          <button 
            onClick={handleConfirm}
            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
          >
            Confirm and Share
          </button>
          <button 
            onClick={handleUploadAnswerKey}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md flex items-center"
          >
            <Upload size={20} className="mr-2" />
            Upload Answer Key
          </button>
        </div>
      </div>

      {/* Add Question Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={addQuestionModalRef} className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Add New Question</h2>
            <select
              value={newQuestion.type}
              onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
              className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select question type</option>
              <option value="text">Text</option>
              <option value="mcq">Multiple Choice</option>
              <option value="tf">True/False</option>
            </
select>
            <textarea
              value={newQuestion.content}
              onChange={(e) => setNewQuestion({ ...newQuestion, content: e.target.value })}
              placeholder="Enter your question here"
              className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
            />
            {newQuestion.type === 'mcq' && (
              <div className="space-y-3 mb-6">
                {newQuestion.options.map((option, index) => (
                  <div key={index} className="flex items-center">
                    <span className="w-8 text-gray-500 font-semibold">{String.fromCharCode(65 + index)}.</span>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleMcqOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
            )}
            <input
              type="number"
              value={newQuestion.marks}
              onChange={(e) => setNewQuestion({...newQuestion, marks: parseInt(e.target.value) || 0})}
              className="w-full p-3 mb-6 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Marks"
            />
            <div className="flex justify-end gap-4">
              <button 
                onClick={handleSaveNewQuestion}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
              >
                Add
              </button>
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors shadow-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Answer Key Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Upload Answer Key</h2>
              <button 
                onClick={() => setIsUploadModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose Answer Key File
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-12 h-12 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">DOC, DOCX, PDF, XLS, XLSX (MAX. 10MB)</p>
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    onChange={handleFileUpload}
                    accept=".doc,.docx,.pdf,.xls,.xlsx"
                  />
                </label>
              </div>
              {uploadedFile && (
                <p className="mt-2 text-sm text-gray-500">
                  Selected file: {uploadedFile.name}
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveUploadedFile}
                disabled={!uploadedFile}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload Answer Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

